import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  isPslfDebugActive,
  getPslfDebugState,
  buildMockLoanSummary,
  annualDebugForms,
  annualDebugProfileAttributes,
  DEBUG_RECOMMENDATION,
} from 'tio-employee/utils/pslf/debug-scenarios';
import type {
  PslfDebugState,
  DebugLoan,
  DebugForm,
  DebugAnnualScenario,
} from 'tio-employee/utils/pslf/debug-scenarios';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { Store as StoreService } from '@warp-drive/core';

export interface DashboardModel {
  accounts: PersonModel['accounts'];
  loanSummary: LoanSummary;
  mostRecentRecommendation: RecommendationModel | undefined;
  person: PersonModel;
  pslfProfile: PslfProfileModel;
  pslfForms: PslfFormModel[];
  debugLinkLoans: boolean;
}

export default class PslfDebugService extends Service {
  @service declare store: StoreService;

  /** Set by {@link applyTo}: the profile on screen carries scenario annual fields, so the
   * dashboard forces its annual surfaces on and the opt-out controls stay off the API. */
  @tracked isOverridingAnnual = false;

  isActive() {
    return isPslfDebugActive();
  }

  getState(): PslfDebugState {
    return getPslfDebugState();
  }

  /** Debug stand-in for the opt-out PATCH: rewrites the pushed profile, leaving the API untouched. */
  setAnnualOptedOut(profile: PslfProfileModel, optedOut: boolean): void {
    const state = this.getState();
    let scenario: DebugAnnualScenario = optedOut ? 'optedOut' : 'scheduled';
    if (!optedOut && state.annualAutoSubmit === 'ineligible') scenario = 'ineligible';

    this.pushProfileAttributes(
      profile,
      annualDebugProfileAttributes({ ...state, annualAutoSubmit: scenario })
    );
  }

  /**
   * Apply all debug overrides to the route model in a single call.
   * Pushes mock records into the Ember Data store (forms, accounts, loans,
   * recommendation) and returns the mutated model.
   */
  applyTo(model: DashboardModel): DashboardModel {
    const debugState = this.getState();
    const result = { ...model };

    // 1. Push debug PSLF forms into the store
    result.pslfForms = this.pushDebugForms(model.pslfProfile, annualDebugForms(debugState));

    // 1b. Annual auto-submission fields the API would have served
    this.isOverridingAnnual = debugState.annualAutoSubmit !== 'off';
    if (this.isOverridingAnnual) {
      this.pushProfileAttributes(model.pslfProfile, annualDebugProfileAttributes(debugState));
    }

    // 2. Mock loan summary (drives recommendation logic)
    result.loanSummary = buildMockLoanSummary(debugState);

    // 3. Mock recommendation (estimator results)
    if (debugState.estimatorCompleted || debugState.recertifyIncome) {
      result.mostRecentRecommendation = this.pushDebugRecommendation();
    } else {
      result.mostRecentRecommendation = undefined;
    }

    // 4. NSLDS accounts
    if (debugState.hasNsldsData) {
      this.pushDebugAccounts(model.person, debugState);
    } else {
      // Debug mode + NSLDS off => unload accounts to simulate "no loans"
      this.unloadAccounts(model.person);
    }

    // 5. Link loans flag
    result.debugLinkLoans = debugState.linkLoans;

    return result;
  }

  private pushProfileAttributes(
    pslfProfile: PslfProfileModel,
    attributes: Record<string, unknown>
  ): void {
    this.store.push({
      data: {
        id: pslfProfile.id,
        type: 'pslf-profile',
        attributes,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  private pushDebugForms(pslfProfile: PslfProfileModel, debugForms: DebugForm[]): PslfFormModel[] {
    if (debugForms.length === 0) return [];

    // Unload prior debug forms
    const existing = pslfProfile.pslfForms?.slice() ?? [];
    for (const form of existing) {
      if ((form.id as string).startsWith('debug-form-')) {
        form.unloadRecord();
      }
    }

    // Push each debug form as a real pslf-form record
    for (const debugForm of debugForms) {
      const startDate = debugForm.employmentStartDate ?? '2024-01-15';
      const borrowerFields: Record<string, string> = {
        'borrower-employer-name': debugForm.employerName,
        'borrower-employment-start-date': startDate,
      };
      if (debugForm.formType === 'priorEmployer' && debugForm.employmentEndDate) {
        borrowerFields['borrower-employment-end-date'] = debugForm.employmentEndDate;
      }

      this.store.push({
        data: {
          id: debugForm.id,
          type: 'pslf-form',
          attributes: {
            status: debugForm.status,
            formType: debugForm.formType,
            borrowerCompletedAt: debugForm.borrowerCompletedAt,
            employerCompletedAt: debugForm.employerCompletedAt,
            submittedAt: debugForm.submittedAt,
            createdAt: debugForm.borrowerCompletedAt ?? new Date().toISOString(),
            borrowerFields,
            employerFields: {},
            isNonPartner: debugForm.nonPartner ?? false,
          },
          relationships: {
            pslfProfile: { data: { type: 'pslf-profile', id: pslfProfile.id } },
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }

    return debugForms
      .map((df) => this.store.peekRecord('pslf-form', df.id) as PslfFormModel)
      .filter(Boolean);
  }

  private unloadAccounts(person: PersonModel): void {
    const accounts = person.accounts?.slice() ?? [];
    for (const account of accounts) {
      for (const loan of account.loans?.slice() ?? []) {
        loan.unloadRecord();
      }
      account.unloadRecord();
    }
  }

  private pushDebugAccounts(person: PersonModel, debugState: PslfDebugState): void {
    this.unloadAccounts(person);

    // Group loans by servicer so each servicer gets its own account + financial institution
    const loansByServicer = new Map<string, { debugLoan: DebugLoan; index: number }[]>();
    debugState.debugLoans.forEach((debugLoan, index) => {
      const key = debugLoan.servicer ?? 'Mohela';
      const group = loansByServicer.get(key) ?? [];
      group.push({ debugLoan, index });
      loansByServicer.set(key, group);
    });

    let groupIndex = 0;
    for (const [servicerName, loans] of loansByServicer) {
      const accountId = `debug-nslds-${person.id}-${groupIndex}`;
      const financialInstitutionId = `debug-fi-${person.id}-${groupIndex}`;

      const loanResources = loans.map(({ debugLoan, index }) => ({
        id: `debug-loan-${index + 1}`,
        type: 'loan',
        attributes: {
          isActive: true,
          observation: {
            'loan-name': debugLoan.name,
            at: new Date().toISOString(),
            balance: debugLoan.currentBalance,
            'principal-balance': debugLoan.currentBalance,
            'interest-rate': 5.5,
            'forgiveness-program-qualified': debugLoan.qualifyingPayments,
            'is-paid-off': false,
            'loan-type': debugLoan.loanType,
            'repayment-plan-derived': 'ibr',
            status: 'active',
            'status-derived': 'LoanStatus.REPAYMENT',
          },
        },
        relationships: {
          account: { data: { type: 'account', id: accountId } },
        },
      }));

      const financialInstitutionResource = {
        id: financialInstitutionId,
        type: 'financial-institution',
        attributes: {
          name: servicerName,
          isActive: true,
        },
      };

      const accountBalance = loans.reduce((sum, l) => sum + l.debugLoan.currentBalance, 0);

      this.store.push({
        data: {
          id: accountId,
          type: 'account',
          attributes: {
            isActive: true,
            observation: {
              provider: 'ObservableProvider.NSLDS',
              balance: accountBalance,
            },
          },
          relationships: {
            person: { data: { type: 'person', id: person.id } },
            financialInstitution: {
              data: { type: 'financial-institution', id: financialInstitutionId },
            },
            loans: {
              data: loanResources.map((l) => ({ type: 'loan', id: l.id })),
            },
          },
        },
        included: [...loanResources, financialInstitutionResource],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      groupIndex++;
    }
  }

  private pushDebugRecommendation(): RecommendationModel {
    const id = 'debug-recommendation';
    const existing = this.store.peekRecord('recommendation', id) as RecommendationModel | undefined;
    if (existing) existing.unloadRecord();

    this.store.push({
      data: {
        id,
        type: 'recommendation',
        attributes: {
          hasErrorInCalcs: false,
          requestingApp: 'PSLF',
          createdAt: new Date().toISOString(),
          bestPlanSummary: {
            'payoff-amount': [DEBUG_RECOMMENDATION.bestPayoffAmount],
            'term-months': [DEBUG_RECOMMENDATION.bestTermMonths],
            'monthly-payments': [DEBUG_RECOMMENDATION.bestMonthlyPayments],
            'total-interest-paid': [DEBUG_RECOMMENDATION.bestTotalInterestPaid],
            'estimated-amount-forgiven': [DEBUG_RECOMMENDATION.bestRemLoanBalance],
          },
          currentPlanSummary: {
            'payoff-amount': [DEBUG_RECOMMENDATION.currentPayoffAmount],
            'term-months': [DEBUG_RECOMMENDATION.currentTermMonths],
            'monthly-payments': [DEBUG_RECOMMENDATION.currentMonthlyPayments],
            'total-interest-paid': [DEBUG_RECOMMENDATION.currentTotalInterestPaid],
            'estimated-amount-forgiven': [DEBUG_RECOMMENDATION.currentRemLoanBalance],
          },
          bestPlanNextSteps: [],
          bestPlanTableHeadings: [],
          currentPlanTableHeadings: [],
          inputSummary: {},
          outputSummary: {},
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);

    return this.store.peekRecord('recommendation', id) as RecommendationModel;
  }
}
